// ./src/utils/analytics/purchase.ts
//
// THE PURCHASE EVENT (owner, 2026-10-27: "send analytics event purchase with the amount and currency of purchased plan or
// addon or service") — GA4's own e-commerce `purchase`, so revenue, ROAS and the Ads conversion read it without set-up:
// `transaction_id`, `value`, `currency`, and ONE item naming what was bought and its kind.
//   • a subscription PLAN — sent when the host is back from Stripe's checkout and the account is really on it;
//   • an ADD-ON bundle, a SERVICE, an EVENT UPGRADE — sent when Stripe confirms the card in the drawer, or when a code made
//     it free (value 0 — still a purchase, with no revenue);
// ⚠ What was CHARGED, never the list price: a discount is in `value`, the code in `coupon`.
// ⚠ Once per transaction (`trackPurchase` keeps the ids it sent this session): a re-render, a retry or a Back must never
//   count a sale twice — GA de-duplicates by `transaction_id` too, but only for the same property and day.
// ⚠ No personal data: ids of the PAYMENT and the PRODUCT only.
// ⚠ Pure, so `check-analytics` RUNS the builder.
import type { AnalyticsEventParams } from './types';

export const PURCHASE_EVENT = 'purchase';

export const PURCHASE_KIND = {
  PLAN: 'plan',
  ADDON: 'addon',
  SERVICE: 'service',
  EVENT_UPGRADE: 'event_upgrade',
} as const;

export type PurchaseKind = (typeof PURCHASE_KIND)[keyof typeof PURCHASE_KIND];

export interface PurchaseFacts {
  /** The payment's own id (a Stripe PaymentIntent, a subscription) — what GA de-duplicates by. */
  transactionId: string | null | undefined;
  kind: PurchaseKind;
  /** The product: a SKU, a plan code. */
  itemId: string | null | undefined;
  itemName?: string | null;
  /** What was charged, as a decimal (0 for a code that made it free). */
  amount: number | null | undefined;
  /** ISO 4217, any case. */
  currency: string | null | undefined;
  /** The discount code, when one was applied. */
  coupon?: string | null;
}

const clean = (value: unknown, max: number): string => String(value ?? '').trim().slice(0, max);

/** The event's parameters, or null when a fact is missing or malformed (nothing is sent rather than a wrong sale). */
export const purchaseParams = (p: PurchaseFacts): AnalyticsEventParams | null => {
  const transactionId = clean(p.transactionId, 100);
  const itemId = clean(p.itemId, 100);
  // ⚠ Checked WHOLE, before any cut: "dollars" cut to "DOL" would pass as a currency.
  const currency = String(p.currency ?? '').trim().toUpperCase();
  // ⚠ A missing amount is not 0 — `Number(null)` is, and would report a free sale. A free grant passes 0 itself.
  const value = p.amount == null || String(p.amount).trim() === '' ? Number.NaN : Number(p.amount);

  if (!transactionId || !itemId || !/^[A-Z]{3}$/.test(currency) || !Number.isFinite(value) || value < 0) {
    return null;
  }

  const price = Math.round(value * 100) / 100;
  const coupon = clean(p.coupon, 100);
  const itemName = clean(p.itemName, 100);

  return {
    transaction_id: transactionId,
    value: price,
    currency,
    ...(coupon ? { coupon } : {}),
    items: [{ item_id: itemId, ...(itemName ? { item_name: itemName } : {}), item_category: p.kind, price, quantity: 1 }],
  };
};

/**
 * #60 — THE UPGRADE FUNNEL ABOVE THE SALE, as GA4's own e-commerce steps: the plans were SEEN (`view_item_list`), a plan
 * was CHOSEN (`begin_checkout`) — then `purchase`. Each says which prompt opened the plans (`plans_source`), so the owner
 * can see which prompt sells: Publish, going live, a page that refused the host, or the dashboard itself.
 * ⚠ No personal data and no event id: the products, the prices shown, the source.
 */
export const UPGRADE_FUNNEL_EVENT = {
  VIEW: 'view_item_list',
  CHECKOUT: 'begin_checkout',
} as const;

export const PLANS_SOURCE = {
  PUBLISH: 'publish',
  GO_LIVE: 'go_live',
  /** A page or panel the event's plan does not include (its "Upgrade this event" button). */
  LOCKED_PAGE: 'locked_page',
  /** The dashboard's own way in: the plan badge, the menu, the offer banner. */
  DASHBOARD: 'dashboard',
} as const;

export type PlansSource = (typeof PLANS_SOURCE)[keyof typeof PLANS_SOURCE];

/** Which prompt opened the plans stage, from its URL: `?then=` (Publish / go live) wins, `?back=` is a refusing page. */
export const plansSourceOf = (then: string | null | undefined, back: string | null | undefined): PlansSource => {
  if (then === 'publish') {
    return PLANS_SOURCE.PUBLISH;
  }

  if (then === 'golive') {
    return PLANS_SOURCE.GO_LIVE;
  }

  return back ? PLANS_SOURCE.LOCKED_PAGE : PLANS_SOURCE.DASHBOARD;
};

/** One plan as the drawer offers it. */
export interface UpgradeFunnelItem {
  sku: string | null | undefined;
  name?: string | null;
  /** The price shown (what this event would pay — the offer, a step up's difference), as a decimal. */
  amount?: number | null;
  currency?: string | null;
}

const itemOf = (p: UpgradeFunnelItem) => {
  const itemId = clean(p.sku, 100);
  const itemName = clean(p.name, 100);
  const price = p.amount == null || !Number.isFinite(Number(p.amount)) ? undefined : Math.round(Number(p.amount) * 100) / 100;

  return itemId ? { item_id: itemId, ...(itemName ? { item_name: itemName } : {}), item_category: PURCHASE_KIND.EVENT_UPGRADE, ...(price != null ? { price } : {}), quantity: 1 } : null;
};

/** `view_item_list` — the plans this event was offered. Null when nothing was for sale (nothing is sent). */
export const upgradeViewParams = (items: UpgradeFunnelItem[], source: PlansSource): AnalyticsEventParams | null => {
  const list = items.map(itemOf).filter((i): i is NonNullable<ReturnType<typeof itemOf>> => !!i);

  return list.length ? { item_list_id: 'event_plans', item_list_name: 'Event plans', plans_source: source, items: list } : null;
};

/** `begin_checkout` — the plan chosen, at the price shown. Null when a fact is missing (nothing is sent). */
export const upgradeCheckoutParams = (item: UpgradeFunnelItem, source: PlansSource, coupon?: string | null): AnalyticsEventParams | null => {
  const line = itemOf(item);
  const currency = String(item.currency ?? '').trim().toUpperCase();

  if (!line || line.price == null || !/^[A-Z]{3}$/.test(currency)) {
    return null;
  }

  const code = clean(coupon, 100);

  return { currency, value: line.price, ...(code ? { coupon: code } : {}), plans_source: source, items: [line] };
};
